import { Box, Card, Chip, IconButton, Tooltip, Typography } from '@mui/material'
import { ReactElement, memo } from 'react'
import { Edit, Delete, Image, AccessTime, Widgets } from '@mui/icons-material'
import MigrationStatusBadge from './migration-status-badge'
import { LightweightCourseV5 } from '../types'
import { useCourseDisplay } from '../hooks/use-course-display'
import { useCourseActions } from '../hooks/use-course-actions'

type CourseLineItemProps = {
  course: LightweightCourseV5
  categoryName: string
  onDelete: (course: LightweightCourseV5) => void
}

let CourseLineItem = memo(
  ({ course, categoryName, onDelete }: CourseLineItemProps): ReactElement => {
    let { thumbnailUrl, migrationStatus, navigationUrl } =
      useCourseDisplay(course)
    let { handleEdit, handleDelete } = useCourseActions({
      course,
      navigationUrl,
      onDelete,
    })

    return (
      <Card
        className="flex flex-col transition-shadow duration-200 sm:flex-row sm:items-center"
        sx={{ bgcolor: 'background.paperDark' }}
      >
        <Box
          className="flex items-center justify-center shrink-0 w-full h-20 border-b border-r-0 sm:w-[120px] sm:h-[120px] sm:border-b-0 sm:border-r"
          sx={{
            bgcolor: 'background.paperDark',
            borderColor: 'divider',
          }}
        >
          {thumbnailUrl ? (
            <img
              src={thumbnailUrl}
              alt={`Course thumbnail for ${course.name}`}
              className="object-cover w-full h-full"
            />
          ) : (
            <Image
              className="text-[32px] sm:text-5xl"
              sx={{
                color: 'text.disabled',
              }}
              aria-hidden="true"
            />
          )}
        </Box>
        <Box className="flex flex-col flex-1 gap-3 p-3 sm:flex-row sm:flex-wrap sm:items-center sm:gap-4 sm:p-4">
          <Box className="flex-1 min-w-0">
            <Box className="flex flex-wrap items-center gap-2 mb-2">
              <Typography
                variant="h6"
                component="h3"
                className="truncate font-semibold text-base sm:text-xl"
              >
                {course.name}
              </Typography>
              <Chip
                label={course.published === 'published' ? 'Published' : 'Draft'}
                color={course.published === 'published' ? 'success' : 'warning'}
                size="small"
              />
            </Box>

            <Box className="flex flex-wrap items-center gap-3 sm:gap-4">
              <Box className="flex items-center">
                <AccessTime
                  fontSize="small"
                  className="mr-1"
                  sx={{
                    color: 'text.secondary',
                  }}
                />
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {course.length} Hour{course.length !== 1 ? 's' : ''}
                </Typography>
              </Box>
              {categoryName && (
                <Box className="flex items-center gap-1">
                  <Widgets
                    fontSize="small"
                    sx={{ color: 'text.secondary' }}
                    aria-hidden="true"
                  />
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {categoryName}
                  </Typography>
                </Box>
              )}
              <MigrationStatusBadge status={migrationStatus} />
            </Box>
          </Box>

          <Box className="flex gap-1 sm:w-auto">
            <Tooltip title="Edit">
              <IconButton
                size="small"
                onClick={handleEdit}
                aria-label={`Edit ${course.name}`}
              >
                <Edit fontSize="medium" color="primary" />
              </IconButton>
            </Tooltip>

            <Tooltip
              title={
                course.hasStudentActivity
                  ? 'Cannot delete a course with student activity'
                  : course.published === 'published'
                    ? 'Unpublish the course before deleting'
                    : 'Delete'
              }
            >
              <span>
                <IconButton
                  size="small"
                  color="error"
                  onClick={handleDelete}
                  disabled={
                    course.published === 'published' ||
                    course.hasStudentActivity
                  }
                  aria-label={`Delete ${course.name}`}
                >
                  <Delete fontSize="medium" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </Box>
      </Card>
    )
  },
  (prevProps, nextProps) =>
    prevProps.course.id === nextProps.course.id &&
    prevProps.course.name === nextProps.course.name &&
    prevProps.course.published === nextProps.course.published &&
    prevProps.course.length === nextProps.course.length &&
    prevProps.course.thumbnail === nextProps.course.thumbnail &&
    prevProps.course.classCount === nextProps.course.classCount &&
    prevProps.course.hasStudentActivity ===
      nextProps.course.hasStudentActivity &&
    prevProps.categoryName === nextProps.categoryName,
)

export default CourseLineItem
